import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles/ScenarioCard.module.css";
import blankImage from "../../../images/network2.jpg";
import AuthContext from "../../../context/AuthContext";
import ShareModal from "./ShareModal";
import TransferModal from "./TransferModal";
import ScenarioCardOptions from "./ScenarioCardOptions";
import ScenarioCardForum from "./ScenarioCardForum";
import useScenarioPin from "../../../hooks/useScenarioPin";


const ScenarioCard = ({
    scenario,
    isAuthor,
    onDeleteScenario,
    onCopyScenario,
    searchQuery,
    isShared,
    onSavedChange,
    onPinChange,
}) => {
    const navigate = useNavigate();
    const { userId } = useContext(AuthContext);

    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);

    // --- REMOVED ---
    // WHY: The ShareModal is now self-contained, managing its own feedback state.
    // This simplifies ScenarioCard, reducing its state management burden and
    // improving component encapsulation. This component's responsibility
    // is to show or hide the modal, not to manage its internal transient UI state.

    // Pin logic — status is loaded lazily when the options menu opens
    const { isPinned, loadPinStatus, onPinScenario, errorMessage, setErrorMessage } = useScenarioPin(scenario._id, onPinChange);

    const [openedDropdown, setOpenedDropdown] = useState(null);
    const [isHovered, setHovered] = useState(false);

    useEffect(() => {
        setIsImageLoaded(false);
    }, [scenario._id]);

    
    const highlightText = (text, query) => {
        if (!text) return "";
        if (!query) return text;

        try {
            const parts = text.split(new RegExp(`(${query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, "gi"));
            return parts.map((part, index) =>
                part && query && part.toLowerCase() === query.toLowerCase() ? (
                    <span key={index} className={styles.highlight}>
                        {part}
                        _</span>
                ) : (
                    part
                )
            );
        } catch (e) {
            console.error("Error creating regex for highlighting:", e);
            return text;
        }
    };


    const handleTagClick = (tag) => {
        navigate(`/browse/${encodeURIComponent(tag)}`);
    };

    const renderImage = () => {
        const imageUrl = scenario.image || blankImage;
        const imageStyle = {
            cursor: scenario.isPublished ? "pointer" : "default"
        };

        const handleImageLoad = () => {

            setIsImageLoaded(true);
        };

        const handleImageError = (e) => {

            setIsImageLoaded(true);
            if (e.target.src !== blankImage) {
                e.target.src = blankImage;
            }
        };

        return (
            <img
                src={imageUrl}
                className={`${styles.image} ${isImageLoaded ? styles.imageLoaded : ''}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
                alt={scenario.title || "Scenario image"}
                style={imageStyle}
                onClick={() => scenario.isPublished && navigate(`/about/${scenario._id}`)}
            />
        );
    };

    const shareUrl = `${window.location.origin}/about/${scenario._id}`;


    return (
        <>
            <article className={styles.card} aria-labelledby={`scenario-title-${scenario._id}`}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{ position: 'relative' }}
            >
                            
            <div className={styles.topRightActions}>
                    
                {isAuthor && (
                    <div className={styles.optionsButtonContainer}>
                        <button
                            className={styles.optionsButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                const opening = openedDropdown !== scenario._id;
                                if (opening) {
                                    loadPinStatus(); // fetch pin status only when the menu is opened
                                }
                                setOpenedDropdown(opening ? scenario._id : null);
                            }}
                            aria-haspopup="true"
                            aria-expanded={openedDropdown === scenario._id}
                            aria-label="Scenario Options"
                        >
                            ⋮
                        </button>
                        {openedDropdown === scenario._id && (
                            <ScenarioCardOptions
                                scenario={scenario}
                                onClose={() => setOpenedDropdown(null)}
                                onDeleteScenario={onDeleteScenario}
                                onCopyScenario={onCopyScenario}
                                navigate={navigate}
                                isShared={isShared}
                                isPinned={isPinned}
                                onPinScenario={onPinScenario}

                            />
                        )}
                        
                        </div>
                )}
                </div>
                {/* Error Message for beyond 3 pin scenario attempts; Outside topRightActions container as it cause issue with some of the scenario's option menu not dimming*/}
                    {errorMessage && (
                         <div className={styles.popupOverlay}>
                             <div className={styles.popup}>
                                 <p>{errorMessage}</p>
                                    <button onClick={() => setErrorMessage(null)}>
                                    OK
                                    </button>
                              </div>
                         </div>
                    )}

                {/* Main content area */}
                <div
                className={`${styles.contentWrapper} ${!scenario.isPublished ? styles.unpublished : ""}`}
                >
                {/* IMAGE LEFT */}
                <div className={styles.imageContainer}>
                    {!scenario.isPublished ? (
                    <div className={styles.tooltipContainer}>
                        {renderImage()}
                        <span className={styles.exclamationIcon} aria-hidden="true">!</span>
                        <div className={styles.tooltipText} role="tooltip">
                        This Scenario is not published
                        </div>
                    </div>
                    ) : (
                    renderImage()
                    )}
                </div>

                {/* TEXT RIGHT */}
                <div className={styles.bottomSection}>
                    <ScenarioCardForum
                        scenario={scenario}
                        userId={userId}
                        isHovered={isHovered}
                        searchQuery={searchQuery}
                        highlightText={highlightText}
                        setShowShareModal={setShowShareModal}
                        setHovered={setHovered}
                        handleTagClick={handleTagClick}
                        onSavedChange={onSavedChange}

                    />
                </div>
                </div>

            </article >

            {/* --- Modals --- */}
            {
                showShareModal && (
                    <ShareModal
                        handleCloseShare={() => setShowShareModal(false)}
                        shareUrl={shareUrl}
                    />
                )
            }
            {
                showTransferModal && (
                    <TransferModal
                        handleCloseTransfer={() => setShowTransferModal(false)}
                        scenario={scenario}
                    />
                )
            }
        </>
    );
};


export default ScenarioCard;